import { HttpParams } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { Client } from '../../../../projects/kiene-core/src/lib/components/client/classes/client';
import { Gruppe } from '../../../../projects/kiene-core/src/lib/components/gruppen/classes/gruppe';
import { GruppenService } from '../../../../projects/kiene-core/src/lib/components/gruppen/gruppen.service';
import { PdfViewerDesktopService } from '../../../../projects/kiene-core/src/lib/components/pdf-viewer/kiene-pdf-viewer/kiene-pdf-viewer-desktop/pdf-viewer-desktop.service';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import {
    FilterItem,
    FilterItemType,
} from '../../../../projects/kiene-core/src/lib/services/backend-api/classes/filter-item';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { KieneTableService } from '../../components/kiene-table/kiene-table.service';
import {
    ArrayLengtTableColumn,
    ConditionSetting,
    ConditionTableColumn,
    DateTableColumn,
    EmailGesendetTableColumn,
    FunctionTableColumn,
    PdfTableColumn,
    TextTableColumn,
} from '../../components/kiene-table/table-column';
import { TableDescriptor, ToolbarButton } from '../../components/kiene-table/table-descriptor';
import { Condition, Operator, TableOption } from '../../components/kiene-table/table-option';
import { Antibiogramm, AntibiogrammAllDownload } from './antibiogramm';
import { AntibiogrammService } from './antibiogramm.service';
import {
    DateRangeDialogComponent,
    DateRangeDialogInput,
} from '../../dialogs/date-range-dialog/date-range-dialog.component';
import { KieneBackendApiService } from '../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { DateRange } from '../date-range';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';

@Component({
    selector: 'kiene-antibiogramm',
    templateUrl: './antibiogramm.component.html',
    styleUrls: ['./antibiogramm.component.scss'],
})
export class AntibiogrammComponent implements OnInit, OnDestroy {
    tableDescriptor: TableDescriptor = new TableDescriptor();
    dateRange: DateRange;
    watchClientSubscription: Subscription;
    currentClient: Client;

    constructor(
        public dialog: MatDialog,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private tableService: KieneTableService,
        private readonly backenApiService: KieneBackendApiService,
        private readonly messageService: MessageService,
        private readonly localStorageService: LocalStorageService,
        private readonly service: AntibiogrammService,
        private readonly pdfViewerService: PdfViewerDesktopService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {
        this.currentClient = this.localStorageService.getCurrentClient();
    }

    ngOnDestroy() {
        this.watchClientSubscription.unsubscribe();
        this.tableService = null;
    }

    ngOnInit() {
        this.watchClientSubscription = this.localStorageService.watchCurrentClient().subscribe(() => {
            this.tableService?.reload();
        });

        this.tableDescriptor.headline = 'Antibiogramme';
        this.tableDescriptor.uniqueIdentifier = 'Antibiogramme';
        this.tableDescriptor.apiUrl = this.apiBaseUrl + 'antibiogramm/';
        this.tableDescriptor.createAllowed = false;
        this.tableDescriptor.standardCreate = false;
        this.tableDescriptor.showDetails = true;
        this.tableDescriptor.nameOfIdField = 'antibiogramm_id';
        this.tableDescriptor.sendCurrentClient = true;

        if (this.router.url.startsWith('/belege')) {
            this.tableDescriptor.sendCurrentClient = true;
            this.tableDescriptor.detailsRoute = '/belege/labor/antibiogramme';
        } else {
            this.tableDescriptor.detailsRoute = '/labor/antibiogramme';
        }

        const befund_id = this.route.snapshot.queryParamMap.get('befund_id');
        if (befund_id) {
            this.tableDescriptor.params = new HttpParams().set('befund_id', befund_id);
        }

        this.tableDescriptor.columns = this.getTableColumns();
        this.tableDescriptor.options = this.getTableOptions();
        this.tableDescriptor.filterItems = this.getFilterItems();
        this.getDateRangeParams();

        this.tableDescriptor.addToolbarButton(
            new ToolbarButton(this.openDateRangeDialog.bind(this), 'date_range', 1811, 'Zeitraum filtern'),
        );
        this.tableDescriptor.permissionView = 1811;
        this.tableDescriptor.permissionCreate = 1812;

        this.tableDescriptor.initialSortColumn = 'editiert';
        this.tableDescriptor.initialSortDirection = 'desc';
        this.tableDescriptor.skipOptionsTablePadding = true;
        this.tableDescriptor.addVoid = this.openNewAntibiogramm.bind(this);
    }

    getFilterItems() {
        const items = [];
        items.push(new FilterItem('Befund nicht abgerechnet', '0', FilterItemType.CHECKBOX, 'rechnung', false));
        if (this.localStorageService.getCurrentUser()?.tierarzt_id) {
            items.push(
                new FilterItem('Nur meine Tierarztgruppe', '1', FilterItemType.CHECKBOX, 'meinetierarztgruppen', true),
            );
        }
        return items;
    }

    getTableColumns() {
        const columns = [];
        columns.push(
            new TextTableColumn('tagebuch_nr', 'Tagebuchnummer', {
                maxWidth: '15ch',
                maxLength: 14,
            }),
        );
        columns.push(
            new TextTableColumn('kunde', 'Kunde', {
                maxWidth: '28ch',
                maxLength: 32,
            }),
        );
        columns.push(
            new TextTableColumn('zusatz', 'Zusatz', {
                maxWidth: '20ch',
                maxLength: 24,
            }),
        );
        columns.push(new ArrayLengtTableColumn('content.testOrderResultRecords', 'Isolate'));
        columns.push(
            new TextTableColumn('tierarztgruppe', 'Tierarztgruppe', {
                maxWidth: '10ch',
                maxLength: 10,
            }),
        );
        columns.push(
            new TextTableColumn('gruppe', 'Gruppe', {
                maxWidth: '14ch',
                maxLength: 16,
            }),
        );
        columns.push(new DateTableColumn('eingang', 'Untersuchungsbeginn', 'dd.MM.yyyy'));
        columns.push(
            new FunctionTableColumn('content', 'Verdacht', this.getVerdachte, {
                maxWidth: '14ch',
                maxLength: 16,
            }),
        );
        columns.push(new DateTableColumn('erstellt', 'Erstellt', 'dd.MM.yyyy HH:mm'));
        columns.push(new DateTableColumn('editiert', 'Aktualisiert', 'dd.MM.yyyy HH:mm'));
        const statusConditions = [];
        statusConditions.push(
            new ConditionSetting([new Condition('status', Operator.GLEICH, 0)], {
                icon: 'fiber_new',
                tooltip: 'erstellt',
            }),
        );
        statusConditions.push(
            new ConditionSetting([new Condition('status', Operator.GLEICH, 1)], { icon: 'fax', tooltip: 'übertragen' }),
        );
        statusConditions.push(
            new ConditionSetting([new Condition('status', Operator.GLEICH, 3)], {
                icon: 'done',
                tooltip: 'Ergebnis vorhanden',
            }),
        );
        statusConditions.push(
            new ConditionSetting([new Condition('status', Operator.GLEICH, -1)], {
                icon: 'note_add',
                tooltip: 'generiert',
            }),
        );

        columns.push(new ConditionTableColumn('status', 'Status', statusConditions));
        columns.push(new PdfTableColumn('pdf_id', 'PDF', this.openPdf.bind(this)));

        columns.push(new EmailGesendetTableColumn());

        return columns;
    }

    getTableOptions() {
        const options = [];
        options.push(new TableOption(this.deleteAntibiogramm.bind(this), 'Antibiogramm löschen', 'delete', 1814));
        options.push(
            new TableOption(this.goToBefund.bind(this), 'zu Befundmitteilung gehen', 'play_arrow', 1311, {
                conditions: [new Condition('befund_id', Operator.GROESSER, 0)],
            }),
        );
        options.push(
            new TableOption(this.recreateAntibiogramm.bind(this), 'Antibiogramm PDF neu erstellen', 'edit', 1816),
        );

        return options;
    }

    gedruckt(anti: Antibiogramm) {
        this.tableService
            .get(
                this.apiBaseUrl + 'antibiogramm/gedruckt.php',
                new HttpParams().set('antibiogramm_id', anti.antibiogramm_id),
            )
            .subscribe({
                next: () => {
                    this.tableService.reload('gedruckt');
                    this.openPdf(anti);
                },
                error: err => {
                    this.messageService.errorMessage('Fehler!', err);
                },
            });
    }

    openNewAntibiogramm() {
        if (this.router.url.startsWith('/belege')) {
            this.router.navigate(['/belege/labor/antibiogramme/neu']).then();
        } else {
            this.router.navigate(['/labor/antibiogramme/neu']).then();
        }
    }

    goToBefund(a: Antibiogramm) {
        this.router.navigate(['labor', 'befundmitteilungen', a.befund_id]).then();
    }

    recreateAntibiogramm(antibiogramm: Antibiogramm) {
        this.service.recreateAntibiogramm(antibiogramm).subscribe({
            next: () => {
                this.messageService.infoMessage(
                    'Antibiogramm PDF ' + antibiogramm.tagebuch_nr + ' erfolgreich neu erstellt!',
                );
                this.tableService.reload('recreateAntibiogramm');
            },
            error: error => {
                console.error(error);
                this.messageService.errorMessage('Fehler ' + antibiogramm.tagebuch_nr + '!', error);
            },
        });
    }

    openPdf(antibiogramm: Antibiogramm) {
        const pdfId = antibiogramm.pdf_id;
        if (pdfId) {
            this.service.showPdf(antibiogramm);
        }
    }

    deleteAntibiogramm(antibiogramm: Antibiogramm) {
        this.service.deleteAntibiogramm(antibiogramm).subscribe({
            next: () => {
                this.messageService.infoMessage(
                    'Befundmitteilung: ' + antibiogramm.tagebuch_nr + ' erfolgreich entfernt!',
                );
                this.tableService.reload('deleteAntibiogramm');
            },
            error: error => {
                console.error(error);
                this.messageService.errorMessage(
                    'Löschen von Antibiogramm: ' + antibiogramm.tagebuch_nr + ' ist fehlgeschlagen!',
                    error,
                );
            },
        });
    }

    getDateRangeParams() {
        this.tableDescriptor.params = new HttpParams();

        this.backenApiService.getSpeicher('antibiogramm_date_range').subscribe({
            next: result => {
                this.dateRange = result;
                this.setParamZeitVon();
                this.setParamZeitBis();
            },
            error: error => {
                this.messageService.errorMessage('Fehler beim Laden des Zeitraums!', error);
            },
        });
    }

    public getVerdachte(content: any): string {
        const verdachte = [];

        if (content) {
            for (const testOrderResultRecord of content?.testOrderResultRecords) {
                if (testOrderResultRecord?.resultRecord?.verdacht) {
                    verdachte.push(testOrderResultRecord.resultRecord.verdacht);
                }
            }
        }
        return verdachte.join(', ');
    }

    openDateRangeDialog() {
        const dateRangeInput = new DateRangeDialogInput();
        dateRangeInput.title = 'Zeitraum filtern';
        dateRangeInput.zeit_von = this.dateRange?.zeit_von;
        dateRangeInput.zeit_bis = this.dateRange?.zeit_bis;

        const dialogRef = this.dialog.open(DateRangeDialogComponent, { data: dateRangeInput });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.confirm) {
                if (!this.tableDescriptor.params) {
                    this.tableDescriptor.params = new HttpParams();
                }
                this.dateRange = result?.range;

                if (this.dateRange) {
                    this.backenApiService.setSpeicher('antibiogramm_date_range', this.dateRange).subscribe({
                        next: () => {
                            this.setParamZeitVon();
                            this.setParamZeitBis();
                            this.tableService.reload('antibiogramm_date_range');
                        },
                        error: error => {
                            console.error(error);
                            this.messageService.errorMessage('Fehler beim Speichern des Zeitraums!', error);
                        },
                    });
                }
            }
        });
    }

    private setParamZeitBis(): void {
        if (this.dateRange?.zeit_bis) {
            this.dateRange.zeit_bis = new Date(this.dateRange.zeit_bis);
            this.dateRange.zeit_bis.setHours(23, 59, 59, 999);
            this.tableDescriptor.params = this.tableDescriptor.params.set(
                'erstellt_bis',
                this.dateRange?.zeit_bis?.toISOString(),
            );
        } else {
            this.tableDescriptor.params = this.tableDescriptor.params.delete('erstellt_bis');
        }
    }

    private setParamZeitVon(): void {
        if (this.dateRange?.zeit_von) {
            this.dateRange.zeit_von = new Date(this.dateRange.zeit_von);
            this.dateRange.zeit_von.setHours(0, 0, 0, 0);
            this.tableDescriptor.params = this.tableDescriptor.params.set(
                'erstellt_von',
                this.dateRange?.zeit_von?.toISOString(),
            );
        } else {
            this.tableDescriptor.params = this.tableDescriptor.params.delete('erstellt_von');
        }
    }

    resetDateRangeFilter() {
        this.dateRange = new DateRange();
        this.backenApiService.setSpeicher('antibiogramm_date_range', this.dateRange).subscribe({
            next: () => {
                this.setParamZeitVon();
                this.setParamZeitBis();
                this.tableService.reload('antibiogramm_date_range');
            },
            error: error => {
                console.error(error);
                this.messageService.errorMessage('Fehler beim Speichern des Zeitraums!', error);
            },
        });
    }
}

@Component({
    selector: 'download-all-antibiogramme-dialog',
    template: `<h3 mat-dialog-title>Alle Antibiogramme zu einem Betrieb herunterladen</h3>
        <mat-dialog-content>
            <h5>Betrieb ausw&auml;hlen</h5>
            <kiene-client-autocomplete
                [panelWidth]="'50%'"
                [showInactiveToggle]="true"
                (selectedClient)="setSelectedClient($event)"
            ></kiene-client-autocomplete>
            <mat-divider></mat-divider>
            <h5>Zeitraum angeben (Probenentnahme)</h5>

            <mat-form-field style="min-width: 40%; padding-left: 10px;">
                <input
                    matInput
                    [matDatepicker]="fromPicker"
                    placeholder="Von"
                    [(ngModel)]="antibiogrammAllDownload.zeit_von"
                />
                <mat-datepicker-toggle matSuffix [for]="fromPicker"></mat-datepicker-toggle>
                <mat-datepicker #fromPicker></mat-datepicker>
            </mat-form-field>
            <mat-form-field style="min-width: 40%; padding-left: 10px;">
                <input
                    matInput
                    [matDatepicker]="toPicker"
                    placeholder="Bis"
                    [(ngModel)]="antibiogrammAllDownload.zeit_bis"
                />
                <mat-datepicker-toggle matSuffix [for]="toPicker"></mat-datepicker-toggle>
                <mat-datepicker #toPicker></mat-datepicker>
            </mat-form-field>
        </mat-dialog-content>
        <mat-dialog-actions>
            <button mat-button color="primary" (click)="onCancelClick()">Abbrechen</button>
            <button mat-raised-button color="primary" (click)="closeDialog()">Herunterladen</button>
        </mat-dialog-actions>`,
})
export class DownloadAllAntibiogrammeDialog implements OnInit {
    antibiogrammAllDownload: AntibiogrammAllDownload = new AntibiogrammAllDownload();

    client: Client;

    gruppeFormCtrl: UntypedFormControl = new UntypedFormControl();
    gruppen: Observable<Gruppe[]>;

    constructor(
        private gruppenService: GruppenService,
        public dialogRef: MatDialogRef<DownloadAllAntibiogrammeDialog>,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {}

    ngOnInit() {
        this.autocompleteGruppen();
    }

    setSelectedClient(c: Client) {
        this.client = c;
    }

    closeDialog() {
        this.antibiogrammAllDownload.client = this.client;
        this.dialogRef.close(this.antibiogrammAllDownload);
    }

    onCancelClick(): void {
        this.dialogRef.close();
    }

    autocompleteGruppen() {
        this.gruppeFormCtrl = new UntypedFormControl();
        this.gruppen = this.gruppeFormCtrl.valueChanges.pipe(
            startWith(''),
            switchMap(value => this.searchGruppen(value)),
        );
    }

    searchGruppen(value: string) {
        return this.gruppenService.searchGruppenPraxissoftware(value).pipe(
            debounceTime(300),
            distinctUntilChanged(),
            map(response => response.records),
        );
    }
}
